/**
 * HOTP/TOTP (RFC 4226, RFC 6238) — codes de verification en deux facteurs.
 *
 * Le calcul est purement local et deterministe : la graine (`entry.ts` §
 * `EntrySecrets.totpSecret`) ne quitte jamais l'appareil, comme tout autre
 * secret d'entree. Primitives exclusivement `@noble/hashes` (HMAC-SHA1/256/512)
 * — aucune crypto maison, conformement a `CLAUDE.md`.
 */
import { hmac } from '@noble/hashes/hmac.js';
import { sha1 } from '@noble/hashes/legacy.js';
import { sha256, sha512 } from '@noble/hashes/sha2.js';
import { assertInteger, base32ToBytes, wipe } from './bytes.js';
import { InvalidParameterError } from './errors.js';

export type TotpAlgorithm = 'sha1' | 'sha256' | 'sha512';

const HASH_FN = Object.freeze({ sha1, sha256, sha512 }) satisfies Readonly<Record<TotpAlgorithm, unknown>>;

/** Defauts Google Authenticator / RFC 6238 : SHA-1, periode 30 s, 6 chiffres. */
export const TOTP_DEFAULTS = Object.freeze({
  algorithm: 'sha1' as TotpAlgorithm,
  digits: 6,
  periodSeconds: 30,
});

/**
 * Steam Guard : TOTP SHA-1 / 30 s dont le code est ecrit en 5 caracteres de cet
 * alphabet au lieu de chiffres decimaux (forme de Steam, reprise a l'identique
 * par Bitwarden, KeePassXC, Aegis, 2FAS et Proton Authenticator).
 */
const STEAM_ALPHABET = '23456789BCDFGHJKMNPQRTVWXY';
export const STEAM_CODE_LENGTH = 5;

/** Codage du code : decimal (RFC 4226) ou Steam Guard. */
export type TotpEncoder = 'steam';

const MIN_DIGITS = 6;
const MAX_DIGITS = 10;
const MIN_PERIOD_SECONDS = 1;
const MAX_PERIOD_SECONDS = 300;

function assertAlgorithm(value: unknown): asserts value is TotpAlgorithm {
  if (value !== 'sha1' && value !== 'sha256' && value !== 'sha512') {
    throw new InvalidParameterError(`algorithm: 'sha1' | 'sha256' | 'sha512' attendu, recu ${String(value)}`);
  }
}

function counterToBytes(counter: number): Uint8Array {
  assertInteger(counter, 'counter', 0, Number.MAX_SAFE_INTEGER);
  const out = new Uint8Array(8);
  const view = new DataView(out.buffer);
  // JS ne manipule que 53 bits surs : on ecrit en deux moities 32 bits.
  const high = Math.floor(counter / 0x100000000);
  const low = counter % 0x100000000;
  view.setUint32(0, high, false);
  view.setUint32(4, low, false);
  return out;
}

/** Troncature dynamique RFC 4226 §5.3 : entier 31 bits extrait du HMAC. */
function dynamicBinCode(mac: Uint8Array): number {
  const offset = mac[mac.length - 1]! & 0x0f;
  return (
    ((mac[offset]! & 0x7f) << 24) |
    ((mac[offset + 1]! & 0xff) << 16) |
    ((mac[offset + 2]! & 0xff) << 8) |
    (mac[offset + 3]! & 0xff)
  );
}

/** Code decimal RFC 4226 §5.3 : `digits` chiffres. */
function dynamicTruncate(mac: Uint8Array, digits: number): string {
  const modulus = 10 ** digits;
  return String(dynamicBinCode(mac) % modulus).padStart(digits, '0');
}

/** Code Steam Guard : le meme entier, ecrit chiffre de poids faible d'abord en base 26. */
function steamTruncate(mac: Uint8Array): string {
  let binCode = dynamicBinCode(mac);
  let code = '';
  for (let i = 0; i < STEAM_CODE_LENGTH; i++) {
    code += STEAM_ALPHABET[binCode % STEAM_ALPHABET.length]!;
    binCode = Math.floor(binCode / STEAM_ALPHABET.length);
  }
  return code;
}

export interface HotpInput {
  /** Graine partagee, octets bruts (deja decodee depuis le base32 stocke). */
  readonly secretBytes: Uint8Array;
  /** Compteur HOTP (entier non negatif). */
  readonly counter: number;
  readonly digits?: number;
  readonly algorithm?: TotpAlgorithm;
  /** `steam` : code Steam Guard (5 caracteres, `digits` ignore). Absent : decimal. */
  readonly encoder?: TotpEncoder;
}

/** HOTP (RFC 4226) : code a un compteur donne. Brique de base de `totp`. */
export function hotp(input: HotpInput): string {
  if (!(input?.secretBytes instanceof Uint8Array) || input.secretBytes.length === 0) {
    throw new InvalidParameterError('secretBytes: Uint8Array non vide attendu');
  }
  const steam = input.encoder === 'steam';
  if (input.encoder !== undefined && !steam) {
    throw new InvalidParameterError(`encoder: 'steam' attendu, recu ${String(input.encoder)}`);
  }
  const digits = input.digits ?? TOTP_DEFAULTS.digits;
  if (!steam) assertInteger(digits, 'digits', MIN_DIGITS, MAX_DIGITS);
  const algorithm = input.algorithm ?? TOTP_DEFAULTS.algorithm;
  assertAlgorithm(algorithm);
  const counterBytes = counterToBytes(input.counter);
  const mac = hmac(HASH_FN[algorithm], input.secretBytes, counterBytes);
  try {
    return steam ? steamTruncate(mac) : dynamicTruncate(mac, digits);
  } finally {
    wipe(mac, counterBytes);
  }
}

/** Parametres effectifs d'une graine stockee (voir `parseTotpSeed`). */
export interface TotpSeed {
  /** Graine base32. */
  readonly secret: string;
  readonly periodSeconds: number;
  readonly digits: number;
  readonly algorithm: TotpAlgorithm;
  /** `steam` : code Steam Guard (`digits` vaut alors `STEAM_CODE_LENGTH`). Absent : decimal. */
  readonly encoder?: TotpEncoder;
}

/**
 * Lit une graine telle que stockee dans `EntrySecrets.totpSecret` : base32 nu
 * (parametres par defaut), ou URI `otpauth://totp/...` (Key URI Format, celle
 * du QR code d'activation) dont `period`, `digits` et `algorithm` sont
 * conserves. Garder l'URI entiere dans le coffre chiffre, c'est ne perdre
 * aucun parametre au changement d'appareil.
 *
 * Steam Guard est reconnu sous ses trois formes repandues : `steam://<base32>`
 * (Bitwarden, Proton Authenticator), `otpauth://totp/...&encoder=steam`
 * (KeePassXC) et `otpauth://steam/...` (Aegis, 2FAS).
 *
 * @throws {InvalidParameterError} URI non `totp` (HOTP refuse : son compteur
 *   devrait etre re-ecrit a chaque code), graine absente ou parametre hors borne
 */
export function parseTotpSeed(value: string): TotpSeed {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new InvalidParameterError('secret: chaine base32 ou URI otpauth non vide attendue');
  }
  if (/^steam:\/\//iu.test(value.trim())) {
    const secret = value.trim().slice('steam://'.length);
    wipe(base32ToBytes(secret));
    return { ...TOTP_DEFAULTS, secret, digits: STEAM_CODE_LENGTH, encoder: 'steam' };
  }
  if (!/^otpauth:/iu.test(value.trim())) return { ...TOTP_DEFAULTS, secret: value };
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    throw new InvalidParameterError('URI otpauth illisible');
  }
  const type = url.hostname.toLowerCase();
  if (type !== 'totp' && type !== 'steam') {
    throw new InvalidParameterError('URI otpauth : seuls les types totp et steam sont pris en charge');
  }
  const steam = type === 'steam' || (url.searchParams.get('encoder') ?? '').toLowerCase() === 'steam';
  const secret = url.searchParams.get('secret') ?? '';
  wipe(base32ToBytes(secret)); // valide la graine des l'enrolement, pas au premier code
  const integer = (name: string, fallback: number, min: number, max: number): number => {
    const raw = url.searchParams.get(name);
    if (raw === null) return fallback;
    if (!/^\d+$/u.test(raw)) throw new InvalidParameterError(`URI otpauth : ${name} entier attendu`);
    const n = Number(raw);
    assertInteger(n, name, min, max);
    return n;
  };
  const algorithm = (url.searchParams.get('algorithm') ?? TOTP_DEFAULTS.algorithm).toLowerCase();
  assertAlgorithm(algorithm);
  if (steam) {
    return {
      secret,
      periodSeconds: integer('period', TOTP_DEFAULTS.periodSeconds, MIN_PERIOD_SECONDS, MAX_PERIOD_SECONDS),
      digits: STEAM_CODE_LENGTH,
      algorithm,
      encoder: 'steam',
    };
  }
  return {
    secret,
    periodSeconds: integer('period', TOTP_DEFAULTS.periodSeconds, MIN_PERIOD_SECONDS, MAX_PERIOD_SECONDS),
    digits: integer('digits', TOTP_DEFAULTS.digits, MIN_DIGITS, MAX_DIGITS),
    algorithm,
  };
}

export interface TotpInput {
  /**
   * Graine telle que stockee dans `EntrySecrets.totpSecret` : base32, ou URI
   * `otpauth://totp` dont les parametres servent de defauts (`parseTotpSeed`).
   */
  readonly secret: string;
  /** Instant UNIX en millisecondes (defaut : horloge courante). */
  readonly timestampMs?: number;
  readonly periodSeconds?: number;
  readonly digits?: number;
  readonly algorithm?: TotpAlgorithm;
}

function totpCounter(timestampMs: number, periodSeconds: number): number {
  return Math.floor(Math.floor(timestampMs / 1000) / periodSeconds);
}

/** TOTP (RFC 6238) : code courant derive de la graine et de l'instant fourni. */
export function totp(input: TotpInput): string {
  const seed = parseTotpSeed(input?.secret);
  const periodSeconds = input.periodSeconds ?? seed.periodSeconds;
  assertInteger(periodSeconds, 'periodSeconds', MIN_PERIOD_SECONDS, MAX_PERIOD_SECONDS);
  const timestampMs = input.timestampMs ?? Date.now();
  assertInteger(timestampMs, 'timestampMs', 0, Number.MAX_SAFE_INTEGER);
  const secretBytes = base32ToBytes(seed.secret);
  try {
    return hotp({
      secretBytes,
      counter: totpCounter(timestampMs, periodSeconds),
      digits: input.digits ?? seed.digits,
      algorithm: input.algorithm ?? seed.algorithm,
      ...(seed.encoder !== undefined ? { encoder: seed.encoder } : {}),
    });
  } finally {
    wipe(secretBytes);
  }
}

/** Secondes restantes avant que le code TOTP courant n'expire (UI : compte a rebours). */
export function totpSecondsRemaining(
  periodSeconds: number = TOTP_DEFAULTS.periodSeconds,
  timestampMs: number = Date.now(),
): number {
  assertInteger(periodSeconds, 'periodSeconds', MIN_PERIOD_SECONDS, MAX_PERIOD_SECONDS);
  assertInteger(timestampMs, 'timestampMs', 0, Number.MAX_SAFE_INTEGER);
  const elapsed = Math.floor(timestampMs / 1000) % periodSeconds;
  return periodSeconds - elapsed;
}

export interface VerifyTotpInput extends TotpInput {
  readonly code: string;
  /**
   * Tolerance de derive d'horloge, en nombre de periodes de part et d'autre de
   * l'instant courant (defaut 1, soit ±30 s pour une periode de 30 s).
   */
  readonly window?: number;
}

/**
 * Verifie un code TOTP saisi par l'utilisateur, avec tolerance de derive
 * d'horloge. Comparaison sur les chaines de chiffres decimales (non secretes
 * une fois generees) : pas d'oracle temporel pertinent ici.
 */
export function verifyTotp(input: VerifyTotpInput): boolean {
  if (typeof input?.code !== 'string' || input.code.length === 0) {
    throw new InvalidParameterError('code: chaine non vide attendue');
  }
  const window = input.window ?? 1;
  assertInteger(window, 'window', 0, 10);
  const periodSeconds = input.periodSeconds ?? parseTotpSeed(input.secret).periodSeconds;
  const timestampMs = input.timestampMs ?? Date.now();
  for (let step = -window; step <= window; step++) {
    const candidate = totp({
      secret: input.secret,
      timestampMs: timestampMs + step * periodSeconds * 1000,
      periodSeconds,
      digits: input.digits,
      algorithm: input.algorithm,
    });
    if (candidate === input.code) return true;
  }
  return false;
}
